import React, { ReactNode } from 'react';
import {
  connect,
  ErrorMessage,
  Field,
  FieldProps,
  Form,
  Formik,
  FormikConfig,
  FormikContextType,
  FormikValues,
  getIn,
  useField,
  useFormikContext,
} from 'formik';
import clsx from 'clsx';
import { LuiButton } from './LuiButton';
import camelcase from 'camelcase';

export function LuiForm(props: {
  children?: React.ReactNode;
  className?: string;
}) {
  const classNames = clsx(
    props.className,
    'lui-standard-form',
    'lui-text-content-area'
  );

  return (
    <div className={classNames}>
      <Form>{props.children} </Form>
    </div>
  );
}

export function LuiFormikForm<
  Values extends FormikValues = FormikValues,
  ExtraProps = {}
>(props: FormikConfig<Values> & ExtraProps & { className?: string }) {
  // this spreads all keys, except className
  const { className, ...formikProps } = { ...props };

  return (
    <Formik {...formikProps}>
      <LuiForm className={props.className}>{props.children}</LuiForm>
    </Formik>
  );
}

export const LuiSelect = connect<
  {
    name: string;
    label: string;
    defaultValue?: string;
    helperInfo?: () => React.Component;
    tooltip?: string;
    validate?: (value: string) => string | undefined;
  } & LuiCommonInputProps
>((props) => {
  const error = getIn(props.formik.errors, props.name);
  const touch = getIn(props.formik.touched, props.name);
  const showError = touch && error;

  return (
    <>
      {props.label && (
        <LuiFormLabel
          for={props.name}
          label={props.label}
          required={props.required}
          tooltip={props.tooltip}
        />
      )}
      {props.helperInfo}
      <div
        className={clsx(
          'lui-margin-top-xs lui-standard-form lui-input-wrapper',
          showError && 'lui-input-error'
        )}
      >
        <ErrorMessage
          name={props.name}
          render={() => (
            <i className="lui-form-status-icon material-icons-round">error</i>
          )}
        />

        <Field
          disabled={props.disabled}
          defaultValue={props.defaultValue}
          name={props.name}
          id={props.name}
          validate={props.validate}
          as="select"
          {...props.inputProps}
        >
          {props.children}
        </Field>

        <ErrorMessage name={props.name} component="p" />
      </div>
    </>
  );
});

export interface LuiCommonInputProps {
  inputProps?: object;
  required?: boolean;
  disabled?: boolean;
}

export function LuiCheckbox(
  props: {
    name: string;
    value: string;
    label: ReactNode;
    htmlFor?: string;
  } & LuiCommonInputProps
) {
  let ctx = useFormikContext();
  return (
    <>
      <Field name={props.name}>
        {({ field }: FieldProps) => {
          const showError = getIn(ctx?.errors, props.name);

          return (
            <div className={clsx(showError && 'lui-input-error')}>
              <div className="lui-input-group-wrapper ">
                {showError && (
                  <i className="lui-form-status-icon material-icons-round">
                    error
                  </i>
                )}
                <div className={clsx('lui-checkbox-container')}>
                  <input
                    {...props.inputProps}
                    type="checkbox"
                    id={props.value}
                    disabled={props.disabled}
                    className="g-checkboxes__input"
                    checked={field.value}
                    {...field}
                  />
                  {typeof props.label === 'string' ? (
                    <label htmlFor={props.value}>{props.label}</label>
                  ) : (
                    props.label
                  )}
                </div>
              </div>
            </div>
          );
        }}
      </Field>
    </>
  );
}

export const LuiFormLabel: React.FC<{
  for: string;
  label: string;
  labelClassName?: string;
  required?: boolean;
  tooltip?: string;
}> = (props) => {
  return (
    <>
      {props.children}
      <label className={props.labelClassName} htmlFor={props.for}>
        {props.required && <span className="lui-required-input-icon">*</span>}
        {props.label}
      </label>
      {props.tooltip && <i className="material-icons-round">help</i>}
    </>
  );
};

export const LuiTextInput = connect<
  {
    name: string;
    label?: string;
    helperInfo?: ReactNode;
    tooltip?: string;
    validate?: (value: string) => string | undefined;
    onValueChange?: (newValue: {
      value: string;
      formik: FormikContextType<any>;
    }) => void;
  } & LuiCommonInputProps
>((props) => {
  const error = getIn(props.formik.errors, props.name);
  const touch = getIn(props.formik.touched, props.name);
  const showError = touch && error;

  return (
    <>
      {props.label && (
        <LuiFormLabel
          required={props.required}
          label={props.label}
          for={props.name}
          tooltip={props.tooltip}
        />
      )}
      {props.helperInfo}
      <Field name={props.name} validate={props.validate}>
        {({ field }: any) => {
          if (props.onValueChange) {
            props.onValueChange({ value: field.value, formik: props.formik });
          }
          return (
            <div
              className={clsx(
                'lui-margin-top-xs lui-standard-form',
                showError && 'lui-input-error'
              )}
            >
              <ErrorMessage
                name={props.name}
                render={() => (
                  <i className="lui-form-status-icon material-icons-round">
                    error
                  </i>
                )}
              />
              <input
                id={props.name}
                {...field}
                type="text"
                {...props.inputProps}
              />
              <ErrorMessage
                name={props.name}
                className={'lui-error-msg'}
                component="p"
              />
            </div>
          );
        }}
      </Field>
    </>
  );
});

export const LuiFormSubmitButton = connect<{
  className?: string;
  disabled?: boolean;
  'data-testid'?: string;
}>((props) => {
  return (
    <LuiButton
      type="submit"
      className={clsx('lui-margin-top-sm', props.className)}
      level="primary"
      data-testid={props['data-testid']}
      disabled={props.disabled}
    >
      {props.children}
    </LuiButton>
  );
});

// TODO: It's annoying that we have to pass name into the RadioGroup and RadioButton
export const LuiRadioGroup: React.FC<{
  hint?: string;
  legend: string;
  name: string;
  required?: boolean;
}> = (props) => {
  let ctx = useFormikContext();
  const error = getIn(ctx.errors, props.name);
  const touch = getIn(ctx.touched, props.name);
  const showError = touch && error;

  const describedby = props.hint && camelcase(props?.hint) + 'Id';
  const errorDescribedBy = error && camelcase(error) + 'Id';

  return (
    <div
      className={clsx(
        'lui-fieldsetBlock-form-group',
        showError && 'lui-input-error'
      )}
    >
      <fieldset
        aria-describedby={clsx(
          props.hint && describedby + 'Id',
          showError && errorDescribedBy
        )}
      >
        <legend>
          <p>
            {props.required && (
              <span className="lui-required-input-icon">*</span>
            )}
            {props.legend}
          </p>
        </legend>
        {showError && (
          <p id={errorDescribedBy}>
            <span className="lui-visually-hidden"> Error: </span>
            {error}
          </p>
        )}
        {props.hint && (
          <p className="lui-fieldset-support-txt small" id={describedby}>
            {props.hint}
          </p>
        )}
        <div className="lui-input-group-wrapper">{props.children}</div>
      </fieldset>
    </div>
  );
};

export const LuiRadioButton: React.FC<
  {
    label: string;
    value: string;
    name: string;
  } & LuiCommonInputProps
> = (props) => {
  const [field, meta] = useField(props);

  return (
    <div>
      {meta.touched && meta.error && (
        <i className="lui-form-status-icon material-icons-round">error</i>
      )}
      <div className="lui-radio-container">
        <input
          disabled={props.disabled}
          {...field}
          checked={props.value === field.value}
          value={props.value}
          id={props.value}
          type="radio"
          {...props.inputProps}
        />
        <label htmlFor={props.value}>{props.label}</label>
      </div>
    </div>
  );
};
